import * as Sentry from '@sentry/react';
import * as types from '../types';
import {
  CouponError,
  GiftCardError,
  ReferralCodeError,
  throwCouponValidationError,
  throwGiftCardValidationError,
  throwReferralCodeValidationError,
} from './errors';
import { fetchCouponDetailsFromRecurly, priceCartForPreview } from './pricing';
import { isAxiosError, webApiClient } from './fi-api/apiUtils';
import { getGwaEntitlementCookie, getGwpEntitlementCookie, hasEntitlementCookie } from './setCouponCookie';
import i18next from '../../i18n';

const VALIDATE_PROMO_CODE_ENDPOINT = '/api/ecommerce/validatecode';
type PromoCodeType = 'coupon' | 'referral' | 'giftCard' | 'internalCoupon';

interface PromoCodeValidationResults {
  promoCodeType: PromoCodeType;
  referrerName?: string;
  referrerAvatarUrl?: string;
  referrerPetAvatarUrl?: string;
}

export interface PromoCodeValidationErrorResponse {
  code: string;
  message: string;
}

type CouponApplicationResult = types.Result<types.CouponDetails, CouponError>;
type GiftCardApplicationResult = types.Result<types.GiftCardDetails, GiftCardError>;
type ReferralCodeApplicationResult = types.Result<types.ReferralDetails, ReferralCodeError>;

/**
 * Confirms a code exists without pricing it. Used for an empty cart, where nothing can be priced and
 * the only question is whether the code is real - and recurly.js is the wrong place to ask. An
 * internal alias is a fi-side identifier that resolves server-side with no recurly coupon behind it,
 * so that lookup reports `not-found` for a perfectly good code. Our own validator knows both kinds.
 */
async function validateCouponWithoutPricing(cart: types.Cart, couponCode: string): Promise<types.CouponDetails> {
  let promoCodeType: PromoCodeType;
  try {
    ({ promoCodeType } = await validatePromoCode(cart, couponCode));
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 400) {
      const validationError = err.response.data?.error;
      if (validationError?.type === 'coupon_code') {
        throwCouponValidationError(validationError);
      }
      throw CouponError.notFound(i18next.t('invalid_coupon_code'));
    }
    // A transport failure says nothing about the code, so don't report it as a bad one.
    throw CouponError.unknown(err);
  }

  if (promoCodeType !== 'coupon' && promoCodeType !== 'internalCoupon') {
    // A gift card or referral code is a real code, but it is not a coupon.
    throw CouponError.notFound(i18next.t('invalid_coupon_code'));
  }

  // `discount` deliberately omitted: it depends on the cart contents, which is what we lack.
  return { code: couponCode };
}

/**
 * Attempt to apply a coupon to the cart.
 *
 * @param reportFailure false for a speculative probe whose failure is expected, so it doesn't land in Sentry as a
 * shopper's coupon error and drown out the spikes those reports exist to catch.
 */
export async function applyCouponToCart(
  cart: types.Cart,
  couponCode: string,
  { reportFailure = true } = {},
): Promise<CouponApplicationResult> {
  try {
    const result = await priceCartForPreview(cart, { applyCoupon: couponCode });
    if (result.validationErrors?.couponCode) {
      throwCouponValidationError(result.validationErrors.couponCode);
    }

    if (result.couponDiscount) {
      return {
        kind: 'success',
        value: { code: couponCode, discount: result.couponDiscount },
      };
    }

    // No discount came back. Recurly can still answer for an ordinary code, but not for the two
    // cases below, where it reports `not-found` for a code that is real:
    //   - an empty cart, where the server skips its eligible-items check so nothing is priced
    //   - an entitled shopper, whose code is an alias with no recurly coupon behind it
    // Ask our own validator instead, which resolves aliases and knows the cart.
    const couponDetails =
      Object.keys(cart.cartItems ?? {}).length === 0 || hasEntitlementCookie()
        ? await validateCouponWithoutPricing(cart, couponCode)
        : await fetchCouponDetailsFromRecurly(couponCode, cart.currency);
    if (!couponDetails) {
      throw CouponError.unknown(new Error(i18next.t('missing_coupon_details')));
    }

    return {
      kind: 'success',
      value: couponDetails,
    };
  } catch (err) {
    // This is a user-facing error but we'll log it to Sentry as well. We like to know about spikes
    // even in coupon errors that are just validation errors (e.g. that coupon doesn't apply to XYZ) as it could
    // indicate a misconfiguration of the coupon. Sentry allows for a more intuitive interface than OpenSearch
    // anomaly detection alerts.
    if (reportFailure) {
      Sentry.captureMessage(err.message, {
        level: 'info',
        tags: {
          messageType: 'promoCodeValidationError',
        },
      });
    }

    if (err instanceof CouponError) {
      return { kind: 'failure', err };
    }

    return { kind: 'failure', err: CouponError.unknown(err) };
  }
}

/**
 * Attempt to apply a gift card to the cart.
 */
export async function applyGiftCardToCart(
  cart: types.Cart,
  redeemedGiftCardCode: string,
): Promise<GiftCardApplicationResult> {
  try {
    const result = await priceCartForPreview(cart, {
      applyGiftCard: redeemedGiftCardCode,
    });
    if (result.validationErrors?.redeemedGiftCardCode) {
      throwGiftCardValidationError(result.validationErrors.redeemedGiftCardCode);
    }

    const giftCardDetails = {
      redemptionCode: redeemedGiftCardCode,
      totalValueOfGiftCardInCents: result.totalValueOfRedeemedGiftCardInCents ?? 0,
    };

    return {
      kind: 'success',
      value: giftCardDetails,
    };
  } catch (err) {
    if (err instanceof GiftCardError) {
      return { kind: 'failure', err };
    }

    return { kind: 'failure', err: GiftCardError.unknown(err) };
  }
}

export async function applyReferralCodeToCart(
  cart: types.Cart,
  referralCode: string,
): Promise<ReferralCodeApplicationResult> {
  try {
    const result = await priceCartForPreview(cart, {
      applyReferralCode: referralCode,
    });
    if (result.validationErrors?.referralCode) {
      throwReferralCodeValidationError(result.validationErrors.referralCode);
    }
    return {
      kind: 'success',
      value: {
        referralCode,
        referrerName: result.referrerName,
        referrerAvatarUrl: result.referrerAvatarUrl,
        referrerPetAvatarUrl: result.referrerPetAvatarUrl,
      },
    };
  } catch (err) {
    if (err instanceof ReferralCodeError) {
      return { kind: 'failure', err };
    }

    return { kind: 'failure', err: ReferralCodeError.unknown(err) };
  }
}

/**
 * Validates a code against all 3 types of code: coupon, referral, gift card
 */
export async function validatePromoCode(cart: types.Cart, promoCode: string): Promise<PromoCodeValidationResults> {
  const { data } = await webApiClient.post<PromoCodeValidationResults>(VALIDATE_PROMO_CODE_ENDPOINT, {
    cartItems: Object.values(cart.cartItems),
    // Codes already attached to the cart
    couponCode: cart.couponCode,
    redeemedGiftCardCode: cart.redeemedGiftCardCode,
    referralCode: cart.referralCode,
    // New code to validate
    validatePromoCode: promoCode,
    currency: cart.currency,
    // Signed GWA entitlement so a rescue alias being validated can resolve to its discount.
    gwaToken: getGwaEntitlementCookie(),
    // Signed GwP partner entitlement so a partner single code can resolve to its discount.
    gwpToken: getGwpEntitlementCookie(),
  });

  return data;
}

/**
 * "Valid" in the context of this function means that the code exists. Other failures when trying to apply
 * a code to a cart (e.g. "Not applicable to any items in cart") are still considered valid for the purposes
 * of this function.
 */
export function isValidCodeResult(
  result: CouponApplicationResult | GiftCardApplicationResult | ReferralCodeApplicationResult,
): boolean {
  if (result.kind === 'failure' && ['not-found', 'unknown'].includes(result.err.type)) {
    return false;
  }

  return true;
}

/**
 * The error type rather than the message: messages are localized through i18next, which makes them
 * useless as an analytics dimension.
 */
export function codeResultFailureType(
  result: CouponApplicationResult | GiftCardApplicationResult | ReferralCodeApplicationResult,
): string {
  return result.kind === 'failure' ? result.err.type : 'none';
}
